<script lang="ts">
export const NUM_COLORS = 10;
</script>

<script setup lang="ts">
import { computed, ref } from "vue";

import { hasValue } from "@/Utils/Objects";
import { hashCode, isValidString } from "@/Utils/String";

export interface AvatarProps {
  src?: string | null;
  alt?: string;
  colorIndex?: number;
  size?: number;
}

const emit = defineEmits<{
  (event: "errorImageLoad"): void;
}>();

const props = withDefaults(defineProps<AvatarProps>(), {
  size: 40,
});

const errorLoadAvatar = ref<boolean>(false);

const color = computed((): string => {
  let index: number | undefined = undefined;

  if (!hasValue(props.colorIndex)) {
    if (isValidString(props.src)) {
      index = Math.abs(hashCode(props.src) % NUM_COLORS);
    } else if (isValidString(props.alt)) {
      index = Math.abs(hashCode(props.alt) % NUM_COLORS);
    } else {
      index = Math.ceil(Math.random() * NUM_COLORS) % NUM_COLORS;
    }
  } else {
    index = Math.abs(props.colorIndex) % NUM_COLORS;
  }

  return `bg-profile-${index + 1}`;
});

const initials = computed((): string => {
  if (isValidString(props.alt)) {
    return props.alt[0]!;
  } else {
    return "";
  }
});

const handleErrorSrcCharge = (): void => {
  emit("errorImageLoad");
  errorLoadAvatar.value = true;
};
</script>

<template>
  <div
    v-if="hasValue(src) && !errorLoadAvatar"
    :style="{ height: `${size}px`, width: `${size}px` }"
    class="avatar"
  >
    <div class="avatar__bg_image" :style="{ backgroundImage: `url(${src})` }" />
    <img :src="src" :alt="alt" @error="handleErrorSrcCharge" :style="{ display: 'none' }" />
  </div>

  <div v-else :style="{ height: `${size}px`, width: `${size}px` }" class="avatar" :class="color">
    <slot>
      <span :class="`name-initials ${size > 40 ? 'size-xl' : 'size-l'}`">{{ initials }}</span>
    </slot>
  </div>
</template>

<style lang="scss">
.avatar {
  align-items: center;
  border-radius: 100px;
  display: flex;
  font-size: 0.75rem;
  justify-content: center;
  text-align: center;

  &__bg_image {
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    height: 100%;
    width: 100%;
    border-radius: 100px;
  }
}

.name-initials {
  @include colors.color(content, high);

  text-transform: uppercase;

  &.size-l {
    @include text.general(high, m);
  }

  &.size-xl {
    @include text.general(high, xl);
  }
}
</style>
